import { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { ConfirmCardSetupData, SetupIntentResult } from '@stripe/stripe-js';

import { EPaymentMethod } from 'enums';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useCreatePaymentMethodMutation, useStripeIntentMutation } from 'store/apis/billing.api';

interface UsePaymentMethodFormManagerProps {
  next?: (data?: any) => void;
}

export const usePaymentMethodFormManager = ({ next = () => {} }: UsePaymentMethodFormManagerProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    orgSlug,
    paymentMethod,
    nameOnPayment: name,
    billingEmail: email,
    cardLastFourCode,
  } = useAppSelector(selectCurrentUserInfo);
  const [setupData, setSetupData] = useState<ConfirmCardSetupData | undefined>(undefined);
  const [paymentElementError, setPaymentElementError] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const [
    stripeIntent,
    {
      isLoading: stripeIntentLoading,
      isSuccess: stripeIntentSuccess,
      isError: stripeIntentError,
      ...restStripeIntentData
    },
  ] = useStripeIntentMutation();

  const [
    createPaymentMethod,
    {
      isLoading: paymentMethodLoading,
      isSuccess: paymentMethodSuccess,
      isError: paymentMethodError,
      ...restPaymentMethodData
    },
  ] = useCreatePaymentMethodMutation();

  const initialValues = {
    paymentMethod,
    name,
    email,
    cardLastFourCode,
  };

  const validationSchema = Yup.object({
    paymentMethod: Yup.string().required('Payment Terms is required'),
    name: Yup.string().nullable().required('Name on card is required'),
    email: Yup.string().when('payment_method', {
      is: EPaymentMethod.INVOICE,
      then: Yup.string().required('Email is required').email('Email must be valid'),
      otherwise: Yup.string().nullable(),
    }),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (!stripe || !elements) {
        return;
      }

      if (formManager.values.paymentMethod === EPaymentMethod.CARD) {
        const { name } = values;

        const card = elements.getElement(CardElement);

        if (card !== null) {
          const setupData: ConfirmCardSetupData = {
            payment_method: {
              card,
              billing_details: {
                name,
                address: {},
              },
            },
          };

          setSetupData(setupData);

          stripeIntent({ orgSlug });
        } else if (cardLastFourCode === '') {
          throw new Error('Credit card number is required.');
        }
      } else if (formManager.values.paymentMethod === EPaymentMethod.INVOICE) {
        const { name, email, paymentMethod: payment_method } = values;

        createPaymentMethod({
          orgSlug,
          data: {
            name,
            email,
            payment_method,
          },
        });
      }
    },
  });

  useEffect(() => {
    if (stripeIntentLoading) {
      setIsUpdating(true);
    }

    if (stripeIntentError) {
      setIsUpdating(false);
    }

    if (!stripeIntentLoading && stripeIntentSuccess && !stripeIntentError) {
      const clientSecret = restStripeIntentData?.data?.intent?.client_secret;

      if (!stripe) {
        return;
      }

      stripe.confirmCardSetup(clientSecret, setupData).then((setupIntentResult: SetupIntentResult) => {
        let payment_method_id = null;

        if (setupIntentResult.setupIntent?.status !== 'succeeded') {
          const error = setupIntentResult.error?.message || 'Unknown Error';
          setPaymentElementError(error);
        } else {
          payment_method_id = setupIntentResult.setupIntent.payment_method;
          setPaymentElementError('');
        }

        if (payment_method_id) {
          createPaymentMethod({
            orgSlug,
            data: {
              name: formManager.values.name,
              payment_method: formManager.values.paymentMethod,
              payment_method_id,
            },
          });
        }
      });
    }
  }, [stripeIntentLoading, stripeIntentSuccess, stripeIntentError]);

  useEffect(() => {
    if (paymentMethodLoading) {
      setIsUpdating(true);
    }

    if (paymentMethodError) {
      setIsUpdating(false);
    }

    if (!paymentMethodLoading && paymentMethodSuccess && !paymentMethodError) {
      const { data } = restPaymentMethodData;

      setIsUpdating(false);

      next(data);
    }
  }, [paymentMethodLoading, paymentMethodSuccess, paymentMethodError]);

  return [
    formManager,
    paymentElementError,
    {
      stripeIntentLoading,
      stripeIntentSuccess,
      stripeIntentError,
      paymentMethodLoading,
      paymentMethodSuccess,
      paymentMethodError,
      isUpdating,
    },
  ] as [
    FormikProps<typeof initialValues>,
    string,
    {
      stripeIntentLoading: boolean;
      stripeIntentSuccess: boolean;
      stripeIntentError: boolean;
      paymentMethodLoading: boolean;
      paymentMethodSuccess: boolean;
      paymentMethodError: boolean;
      isUpdating: boolean;
    },
  ];
};
